import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Trash2, Minus, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useNavigate } from "react-router-dom";

const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

export default function CartSheet() {
  const { items, remove, setQty, totalCents, open, setOpen } = useCart();
  const navigate = useNavigate();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="flex w-full flex-col sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="font-display text-xl">Your cart</SheetTitle>
        </SheetHeader>
        <div className="mt-4 flex-1 overflow-y-auto pr-2">
          {items.length === 0 && (
            <div className="grid h-full place-items-center text-center text-slate-500">
              <div>
                <div className="text-5xl">🌊</div>
                <p className="mt-3 font-display text-lg">Your cart is empty</p>
                <p className="text-sm text-slate-400">Explore the shop for chemicals, tools and gear.</p>
              </div>
            </div>
          )}
          <ul className="space-y-4">
            {items.map((it) => (
              <li key={it.product_id} className="flex gap-3 rounded-xl border border-slate-200 bg-white p-3" data-testid={`cart-item-${it.product_id}`}>
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-slate-100">
                  {it.image_url ? (
                    <img src={it.image_url} alt={it.name} className="h-full w-full object-cover" />
                  ) : null}
                </div>
                <div className="flex flex-1 flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium text-slate-900 line-clamp-2">{it.name}</div>
                      <div className="text-sm text-cyan-700">{fmt(it.price_cents)}</div>
                    </div>
                    <button onClick={() => remove(it.product_id)} className="text-slate-400 hover:text-red-500" data-testid={`cart-remove-${it.product_id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-auto flex items-center gap-2">
                    <button
                      onClick={() => setQty(it.product_id, it.quantity - 1)}
                      className="grid h-7 w-7 place-items-center rounded-md border border-slate-200 hover:bg-slate-50"
                      data-testid={`cart-qty-decrement-${it.product_id}`}
                    ><Minus className="h-3 w-3" /></button>
                    <span className="min-w-[24px] text-center text-sm">{it.quantity}</span>
                    <button
                      onClick={() => setQty(it.product_id, it.quantity + 1)}
                      className="grid h-7 w-7 place-items-center rounded-md border border-slate-200 hover:bg-slate-50"
                      data-testid={`cart-qty-increment-${it.product_id}`}
                    ><Plus className="h-3 w-3" /></button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <SheetFooter className="mt-4 flex-col gap-2 sm:flex-col">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">Subtotal</span>
            <span className="font-display text-lg font-semibold text-slate-900" data-testid="cart-subtotal">{fmt(totalCents)}</span>
          </div>
          <Button
            disabled={items.length === 0}
            onClick={() => { setOpen(false); navigate("/checkout"); }}
            className="btn-aqua w-full"
            data-testid="cart-checkout-button"
          >
            Checkout
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
